import Shell from '../components/Shell';
import Seo from '../components/Seo';
import { PageHero, Section, CTA } from '../components/Pieces';
import { conventionalVsTransformed, journeyStages, siteImages } from '../mock';
import { CheckCircle2, ArrowRight } from 'lucide-react';

export default function SchoolTransformation() {
  return (
    <Shell>
      <Seo title="School Transformation"
        description="CBSE+ keeps the curriculum and transforms pedagogy: learner-centred practice, evidence-based diagnostics, teacher development and continuous classroom improvement."
        image={siteImages.schoolTransformation} />
      <PageHero
        kicker="School Transformation"
        title="From CBSE to CBSE+ — same curriculum, transformed learning."
        sub="CBSE curriculum is retained. Pedagogy and classroom practice are transformed — with learner-centred approaches, evidence-led diagnostics, teacher development and continuous improvement."
        breadcrumbs={[{ label: 'Transform Education' }, { label: 'School Transformation' }]}
        image={siteImages.schoolTransformation}
        imageAlt="A teacher working with students at the blackboard"
      />

      <Section bg="white">
        <div className="grid md:grid-cols-2 gap-10">
          <div>
            <div className="mm-kicker mb-3">What CBSE+ Integrates</div>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-[var(--mm-ink)] mm-rule">Eight capability layers, one institutional programme.</h2>
          </div>
          <div className="grid gap-3">
            {[
              'Learner-centred pedagogy',
              'Competency-based curriculum delivery',
              'Evidence-based school diagnostics',
              'Teacher professional development',
              'Teaching Learning Material development and sharing',
              'Classroom observation and continuous improvement',
              'Academic planning and review',
              'Technology and digital resources',
            ].map((c) => (
              <div key={c} className="flex items-start gap-3 bg-[var(--mm-cream)] border border-[var(--mm-line)] rounded-xl p-4">
                <CheckCircle2 className="w-5 h-5 text-[var(--mm-purple)] mt-0.5 shrink-0"/>
                <span className="text-sm font-medium text-[var(--mm-ink)]">{c}</span>
              </div>
            ))}
          </div>
        </div>
      </Section>

      <Section bg="cream2">
        <div className="text-center max-w-3xl mx-auto">
          <div className="mm-kicker mb-3">Practice Shifts</div>
          <h2 className="font-display text-3xl md:text-5xl font-bold text-[var(--mm-ink)] mm-rule-center">Conventional tendency → Transformed classroom</h2>
        </div>
        <div className="mt-12 grid gap-3">
          {conventionalVsTransformed.map((row, i) => (
            <div key={i} className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] items-center bg-white border border-[var(--mm-line)] rounded-xl p-5 gap-4">
              <div className="text-[var(--mm-ink)]/60 text-sm md:text-base">{row[0]}</div>
              <ArrowRight className="w-4 h-4 text-[var(--mm-purple)] hidden md:block"/>
              <div className="text-[var(--mm-ink)] text-sm md:text-base font-semibold md:text-right">{row[1]}</div>
            </div>
          ))}
        </div>
      </Section>

      <Section bg="white">
        <div className="text-center max-w-3xl mx-auto">
          <div className="mm-kicker mb-3">School Transformation Journey</div>
          <h2 className="font-display text-3xl md:text-5xl font-bold text-[var(--mm-ink)] mm-rule-center">A multi-year institutional capability programme.</h2>
          <p className="mt-6 text-[var(--mm-ink)]/70">Pilots create demonstrable classroom evidence before institutional replication. Leadership owns outcomes, priorities, teacher development and continuous improvement.</p>
        </div>
        <div className="mt-12 grid grid-cols-2 md:grid-cols-6 gap-3">
          {journeyStages.map((s, i) => (
            <div key={s} className="mm-card p-5 text-center">
              <div className="font-display text-2xl font-bold text-[var(--mm-purple)]">{String(i+1).padStart(2,'0')}</div>
              <div className="mt-2 text-sm font-semibold text-[var(--mm-ink)]">{s}</div>
            </div>
          ))}
        </div>
      </Section>

      <CTA title="Bring evidence-led transformation to your school." sub="Start with Discovery and build institutional capability step by step." />
    </Shell>
  );
}
